// src/pages/Users/Produits/ProduitsPage.jsx

import React from "react";
import { FaLaptopCode } from "react-icons/fa";

import useScrollToTop from "../../../hooks/useScollToTop";
import DashboardNavigation from "../../DashboardNavigation/DashboardNavigation";
import { useUser } from "../../Home/UserContext";
import { getRoleFromRoleId } from "../../../utils/roleHelper";
import ScrollIndicator from "../../Home/ScrollIndicator";
import HeaderTitle from "../../../components/HeaderTitle/HeaderTitle";
import Reveal from "./../../../components/Reveal/Reveal";

import useProductsApps from "./hooks/useProductsApps";

import ProductsHero from "./components/ProductsHero";
import DonationCard from "./components/DonationCard";
import AuthorCard from "./components/AuthorCard";
import ProductCard from "./components/ProductCard";
import SkeletonProductCard from "./components/SkeletonProductCard";
import EmptyProductsState from "./components/EmptyProductsState";
import FloatingDonationButton from "./components/FloatingDonationButton";

const DONATION_URL = "https://buy.stripe.com/cN2aFtaqL5fC1AkcMR";

export default function ProduitsPage() {
  useScrollToTop();

  const { user } = useUser();
  const userRole = getRoleFromRoleId(user?.role_id);

  const { products, loading, error, stats, refreshProducts } = useProductsApps();

  return (
    <section className="min-h-screen bg-slate-50 text-slate-950 dark:bg-slate-950 dark:text-slate-50">
      <ScrollIndicator />

      <div className="pt-[96px] sm:pt-[120px] lg:pt-[160px]" aria-hidden />

      <main className="mx-auto w-full max-w-7xl px-3 pb-28 sm:px-6 lg:px-8">
        <HeaderTitle
          role="Utilisateur"
          title="Applications web"
          subtitle="Des outils web pensés pour les clubs, les enseignants, les adhérents et les structures sportives."
          icon={FaLaptopCode}
          align="left"
          className="mb-4"
        />

        <div className="mb-4">
          <DashboardNavigation role={userRole} />
        </div>

        <Reveal>
          <ProductsHero
            productsCount={stats.total}
            demoLinksCount={stats.demoLinks}
            donationUrl={DONATION_URL}
          />
        </Reveal>

        <div className="mt-5 grid gap-4 lg:grid-cols-[1fr_0.85fr]">
          <AuthorCard />

          <DonationCard donationUrl={DONATION_URL} variant="compact" />
        </div>

        <section className="mt-6">
          <div className="mb-4 flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-slate-400">
                Catalogue
              </p>
              <h2 className="mt-1 text-2xl font-bold text-slate-950 dark:text-white">
                Mes applications
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Voici les produits et outils que je développe pour répondre aux
                besoins concrets des clubs sportifs.
              </p>
            </div>

            {!loading && !error && (
              <span className="inline-flex w-fit rounded-full bg-white px-4 py-2 text-xs font-bold text-slate-600 ring-1 ring-slate-200 dark:bg-slate-900 dark:text-slate-300 dark:ring-slate-800">
                {products.length} application{products.length > 1 ? "s" : ""}
              </span>
            )}
          </div>

          {loading && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {Array.from({ length: 6 }).map((_, index) => (
                <SkeletonProductCard key={index} />
              ))}
            </div>
          )}

          {!loading && error && (
            <EmptyProductsState
              title="Applications indisponibles"
              description={error}
              actionLabel="Réessayer"
              onAction={refreshProducts}
            />
          )}

          {!loading && !error && products.length === 0 && (
            <EmptyProductsState
              title="Aucune application trouvée"
              description="Le catalogue est vide pour le moment."
              actionLabel="Recharger"
              onAction={refreshProducts}
            />
          )}

          {!loading && !error && products.length > 0 && (
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
              {products.map((app, index) => (
                <ProductCard key={app.id || `${app.nom_app}-${index}`} app={app} />
              ))}
            </div>
          )}
        </section>

        <div className="mt-6">
          <DonationCard donationUrl={DONATION_URL} />
        </div>
      </main>

      <FloatingDonationButton donationUrl={DONATION_URL} />
    </section>
  );
}